import { Dialog, DialogTrigger, DialogPortal } from "@/components/ui/dialog"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Button } from "@/components/ui/button"
import { X, Trash2 } from "lucide-react"
import { cn } from "@/lib/utils"
import type { PortfolioItem } from "@/types/models"

interface DeleteConfirmationModalProps {
  item: PortfolioItem
  onConfirm: () => void
  onCancel?: () => void
  children: React.ReactNode
  type?: 'flash' | 'tattoo'
}

export function DeleteConfirmationModal({ 
  item, 
  onConfirm, 
  onCancel,
  children,
  type = 'tattoo'
}: DeleteConfirmationModalProps) {
  const handleDelete = () => {
    onConfirm()
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogPortal>
        {/* Custom overlay with sheet-like dimming */}
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        {/* Custom content */}
        <DialogPrimitive.Content
          className={cn(
            "fixed left-0 right-0 top-[50%] z-50 translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
            "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-xl font-semibold text-foreground">
              Delete {type === 'flash' ? 'Flash' : 'Tattoo'}?
            </h2>
            <DialogPrimitive.Close 
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-0 disabled:pointer-events-none"
              onClick={onCancel}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          {/* Image */}
          <div className="px-6 pb-4">
            <div className="rounded-[var(--card-radius)] overflow-hidden">
              <img
                src={item.compressedImageUrl || item.imageUrl}
                alt={item.caption || `${type} design`}
                className="w-full h-auto max-h-[50vh] object-cover object-center"
              />
            </div>
          </div>

          {/* Warning Text */}
          <div className="px-6 pb-4">
            <p className="text-sm text-muted-foreground">
              This will archive the {type} and remove it from your profile. You can restore it later if needed.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              {/* Cancel button */}
              <DialogPrimitive.Close asChild>
                <Button 
                  variant="outline"
                  onClick={onCancel}
                  className="px-8 py-6 text-base font-medium rounded-[var(--card-radius)] w-full sm:w-auto"
                  size="lg"
                >
                  Cancel
                </Button>
              </DialogPrimitive.Close>
              
              {/* Delete button */}
              <DialogPrimitive.Close asChild>
                <Button 
                  variant="destructive"
                  onClick={handleDelete}
                  className="px-8 py-6 text-base font-medium rounded-[var(--card-radius)] w-full sm:w-auto"
                  size="lg"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </DialogPrimitive.Close>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

